import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FileLocation,
  FileLocationExplored,
  FileLocationProviderContext,
  SelectedItemLocation,
} from "./FileLocationProvider";
import { PageDocumentRoots } from "../../api/models/page-document-roots";
import { formatMessage } from "../../utils/helpers/LanguageHelper";

interface InternalState {
  applicationCurrentLocation: FileLocation;
  exploredLocation: FileLocationExplored;
  selectedItemLocation: SelectedItemLocation;
  isLoading: boolean;
  searchQuery: string;
}

export interface WithApiFileLocationProviderProps {
  cleanState?: boolean;
  selectedItem?: SelectedItemLocation;
  getItems: (path?: string) => Promise<FileLocation[]>;
  selectDepth: "FOLDER" | "ITEM";
  pagination?: PageDocumentRoots;
}

export const DEFAULT_FOLDER_CREATION = "Shared Documents";

// Stable identity so consumers keyed on exploredLocation.children don't re-fire on every render.
const EMPTY_CHILDREN: FileLocation[] = [];

export const WithApiFileLocationProvider: React.FunctionComponent<WithApiFileLocationProviderProps> = (props) => {
  const [state, setState] = useState<InternalState>();
  const { cleanState, selectedItem, getItems, selectDepth, pagination } = props;

  // On the render where cleanState flips true the stale state isn't reset yet; hide children for
  // that render so a remounting consumer can't reseed from the stale list.
  const prevCleanStateRef = useRef(cleanState);
  const cleanStateJustBecameTrue = cleanState && !prevCleanStateRef.current;
  useEffect(() => {
    prevCleanStateRef.current = cleanState;
  });

  useEffect(() => {
    if (cleanState) {
      // Clear the stale list before the async reload so old results aren't exposed during it.
      setState((s) => ({
        ...s,
        isLoading: true,
        selectedItemLocation: null,
        exploredLocation: s?.exploredLocation
          ? { ...s.exploredLocation, children: [], page: undefined }
          : s?.exploredLocation,
      }));
      getItems(null).then((loc) => {
        let location = {
          key: DEFAULT_FOLDER_CREATION,
          text: DEFAULT_FOLDER_CREATION,
          document: { folder: true, docName: DEFAULT_FOLDER_CREATION, docDescription: "" },
        };
        setState((s) => ({
          ...s,
          applicationCurrentLocation: location,
          selectedItemLocation: null,
          exploredLocation: {
            item: location,
            path: [],
            children: loc ?? [],
            // Never overwrite with the closure `pagination`: getItems refreshes the parent
            // pagination while this reload runs, so the closure value is the one of the
            // previous query (e.g. the cleared search) and would disable infinite scroll.
            page: s?.exploredLocation?.page ?? pagination,
          },
          isLoading: false,
        }));
      });
    }
  }, [cleanState, getItems]);

  useEffect(() => {
    if (pagination) {
      setState((s) => ({
        ...s,
        exploredLocation: {
          ...s?.exploredLocation,
          children: s?.exploredLocation?.children ?? EMPTY_CHILDREN,
          page: pagination,
        },
      }));
    }
  }, [pagination]);

  console.log("rendering WithApiFileLocationProvider with state", state);

  const navigateRoot = () => {
    console.log("navigateRoot");

    getItems(null).then((loc) => {
      let location = {
        key: DEFAULT_FOLDER_CREATION,
        text: DEFAULT_FOLDER_CREATION,
        document: { folder: true, docName: DEFAULT_FOLDER_CREATION, docDescription: "" },
      };
      setState((s) => ({
        ...s,
        applicationCurrentLocation: location,
        selectedItemLocation: null,
        exploredLocation: {
          item: location,
          path: [],
          children: loc ?? [],
          page: s?.exploredLocation?.page,
        },
      }));
    });
  };

  useEffect(() => {
    if (selectedItem) {
      setState((s) => ({
        ...s,
        applicationCurrentLocation: selectedItem.fileLocationExplored.item,
        selectedItemLocation: null,
        exploredLocation: {
          ...selectedItem.fileLocationExplored,
          children: selectedItem.fileLocationExplored.children ?? EMPTY_CHILDREN,
        },
        isLoading: false,
      }));
    } else {
      navigateRoot();
    }
  }, []);

  const exploreLocation = useCallback(
    (fileLocation: FileLocation) => {
      setState((prev) => ({ ...prev, isLoading: true }));
      getItems(fileLocation.path).then((loc) => {
        setState((prev) => {
          const currentPath = prev?.exploredLocation?.path || [];
          const fileLocationIndexInPath = currentPath.findIndex((item) => item.key === fileLocation.key);
          let newPath = [...currentPath, prev.exploredLocation.item];
          if (fileLocationIndexInPath > -1) {
            newPath = currentPath.slice(0, fileLocationIndexInPath);
          }
          return {
            ...prev,
            exploredLocation: {
              item: fileLocation,
              children: loc ?? [],
              path: newPath,
              page: prev?.exploredLocation?.page,
            },
            selectedItemLocation: selectDepth === "FOLDER" ? prev.selectedItemLocation : null,
            isLoading: false,
          };
        });
      });
    },
    [getItems, selectDepth]
  );

  const selectApplicationCurrentLocation = useCallback(
    (fileLocation: FileLocation) => {
      setState((s) => ({
        ...s,
        applicationCurrentLocation: fileLocation,
      }));
    },
    [state]
  );

  const selectItemLocation = useCallback(
    (fileLocation: FileLocation) => {
      let path = "";
      if (fileLocation != null) {
        if (state.exploredLocation.path?.length) {
          state.exploredLocation.path.forEach((p) => {
            path = path + p.text + ">";
          });
        }
        path = path + state.exploredLocation.item.text + ">";
        path = path + fileLocation.text;
        fileLocation.path = path;
      }
      let selectedItemLocation: SelectedItemLocation = {
        fileLocation: fileLocation,
        fileLocationPath: path,
        fileLocationExplored: state.exploredLocation,
      };
      setState((s) => ({
        ...s,
        selectedItemLocation: selectedItemLocation,
      }));
    },
    [state]
  );

  function updateChildren(loc, page) {
    setState((s) => ({
      ...s,
      exploredLocation: {
        ...s.exploredLocation,
        children: loc ?? [],
        page,
      },
    }));
  }

  const isEmptyItemListing = selectDepth === "ITEM" && !state?.exploredLocation?.children?.length;

  return state && !state.isLoading && !cleanStateJustBecameTrue ? (
    <FileLocationProviderContext.Provider
      value={{
        exploredLocation: state.exploredLocation,
        applicationCurrentLocation: state.applicationCurrentLocation,
        exploreLocation: exploreLocation,
        selectApplicationCurrentLocation: selectApplicationCurrentLocation,
        selectItemLocation: selectItemLocation,
        selectedItemLocation: state.selectedItemLocation,
        updateChildren: updateChildren,
      }}
    >
      {isEmptyItemListing && (
        <div style={{ width: "100%", textAlign: "center", color: "var(--color-blue)" }}>
          <img
            src={"/assets/documentInvalid.svg"}
            alt="There are no documents that match your input text"
            style={{
              width: "100%",
              height: "calc(100vh - 16rem)",
              objectFit: "contain",
            }}
          />
          <span>{formatMessage("cof.base.searchbar.nodocumentsfound")}</span>
        </div>
      )}
      {props.children}
    </FileLocationProviderContext.Provider>
  ) : null;
};
